import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Card } from "../../../components/Card";
import { Files } from "../../../services/FilesService";
import PublicStructuredQuestionItem from "./PublicStructuredQuestionItem";

/**
 * PublicStructuredQuestionResponse — Read-only view of structured question responses
 * for the public results page. Fetches response content from S3 and delegates
 * each question's rendering (including file download) to PublicStructuredQuestionItem.
 *
 * Props:
 * @param {Array}  questions — Array of structured question objects (snake_case from backend)
 * @param {string} measureId — The ID of the qualitative measure
 * @param {string} tenantId  — The tenant ID for public file access
 */
const PublicStructuredQuestionResponse = ({
  questions,
  measureId,
  tenantId,
}) => {
  const [responses, setResponses] = useState(null);
  const [errors, setErrors] = useState({});

  // Fetch response content from S3 for each question
  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const results = await Promise.all(
          questions.map((q) =>
            Files.getPublicFileContent(
              "response.html",
              "public-qualitativeEvidence",
              `${measureId}/${q.id}`,
              tenantId
            )
              .then((res) => ({ content: res.content || "", error: false }))
              .catch(() => ({ content: "", error: true }))
          )
        );

        const fetched = {};
        const fetchedErrors = {};
        questions.forEach((q, i) => {
          fetched[q.id] = results[i].content;
          if (results[i].error) {
            fetchedErrors[q.id] = true;
          }
        });
        setResponses(fetched);
        setErrors(fetchedErrors);
      } catch {
        const empty = questions.reduce((acc, q) => {
          acc[q.id] = "";
          return acc;
        }, {});
        const allErrors = questions.reduce((acc, q) => {
          acc[q.id] = true;
          return acc;
        }, {});
        setResponses(empty);
        setErrors(allErrors);
      }
    };

    fetchResponses();
  }, [measureId, tenantId, questions]);

  if (responses === null) {
    return <Skeleton height="10rem" count={2} style={{ marginBottom: 10 }} />;
  }

  return (
    <Card
      style={{
        backgroundColor: "#F2F8FA",
        border: "1px solid #ccc",
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
      }}
    >
      {questions.map((question, index) => (
        <PublicStructuredQuestionItem
          key={question.id || index}
          question={question}
          index={index}
          measureId={measureId}
          tenantId={tenantId}
          responseText={responses[question.id] || ""}
          hasError={!!errors[question.id]}
          isLast={index === questions.length - 1}
        />
      ))}
    </Card>
  );
};

export default PublicStructuredQuestionResponse;
